import { InventoryTicketTypes, Sale, SalesTicketType } from 'features/common/hooks/useHighlights/types';
import {
	Action,
	AggregateDataObject,
	PayoutBy,
	ProcessTimelineProps,
	TicketTypesPerDate,
	WindowInfo,
	XAxisOption,
} from './types';

export const getWkStart = (dateStr: Date): Date => {
	const date = new Date(dateStr);
	date.setDate(date.getDate() - date.getDay());
	date.setHours(0, 0, 0, 0);
	return date;
};

export const getWkEnd = (dateStr: Date): Date => {
	const date = new Date(dateStr);
	date.setDate(date.getDate() + (6 - date.getDay()));
	date.setHours(23, 59, 59, 999);
	return date;
};

export const getMonthStart = (dateStr: Date): Date => {
	const date = new Date(dateStr);
	date.setDate(1);
	date.setHours(0, 0, 0, 0);
	return date;
};

export const getMonthEnd = (dateStr: Date): Date => {
	const date = new Date(dateStr);
	date.setMonth(date.getMonth() + 1, 0);
	date.setHours(23, 59, 59, 999);
	return date;
};

const getLocalDayStart = (dateStr: Date): Date => {
	const date = new Date(dateStr);
	date.setHours(0, 0, 0, 0);
	return date;
};

/** Snap a date to the start of its basket for the given granularity. */
export const snapBasketStart = (date: Date, xAxis: XAxisOption): Date => {
	if (xAxis === 'weeks') return getWkStart(date);
	if (xAxis === 'months') return getMonthStart(date);
	return getLocalDayStart(date);
};

/** Snap a date to the end of its basket for the given granularity. */
export const snapBasketEnd = (date: Date, xAxis: XAxisOption): Date => {
	if (xAxis === 'weeks') return getWkEnd(date);
	if (xAxis === 'months') return getMonthEnd(date);
	const end = new Date(date);
	end.setHours(23, 59, 59, 999);
	return end;
};

export const aggregateTTSold = (oldAggregate: SalesTicketType[], newData: SalesTicketType[]) => {
	const aggregateObj: AggregateDataObject = {};
	oldAggregate.concat(newData).reduce((newAggregate, ticketType) => {
		const ttData = newAggregate[ticketType.name];
		if (ttData) {
			ttData.sold += ticketType.sold;
			ttData.payout += ticketType.payout;
		} else {
			newAggregate[ticketType.name] = { ...ticketType };
		}
		return newAggregate;
	}, aggregateObj);

	return Object.values(aggregateObj);
};

const aggregateByPeriod = (
	eventHighlights: NonNullable<ProcessTimelineProps['eventHighlights']>,
	getPeriodStart: (d: Date) => Date
) => {
	const salesByPeriod: PayoutBy = {};
	for (const salesDate of eventHighlights.sales) {
		const key = getPeriodStart(salesDate.date).valueOf();
		if (salesByPeriod[key]) {
			salesByPeriod[key].payout += salesDate.payout;
			salesByPeriod[key].ticket_types = aggregateTTSold(salesByPeriod[key].ticket_types, salesDate.ticket_types);
		} else {
			salesByPeriod[key] = { ...salesDate, date: new Date(key) };
		}
	}
	return { ...eventHighlights, sales: Object.values(salesByPeriod) };
};

export const processTimeline = ({ eventHighlights, timeline }: ProcessTimelineProps) => {
	if (!eventHighlights) return undefined;
	if (timeline === 'weeks') return aggregateByPeriod(eventHighlights, getWkStart);
	if (timeline === 'months') return aggregateByPeriod(eventHighlights, getMonthStart);
	return aggregateByPeriod(eventHighlights, getLocalDayStart);
};

export const getTicketsSold = (sales: Sale<Date>[], ticketTypeInv?: InventoryTicketTypes[]) => {
	if (!ticketTypeInv || sales.length === 0) return;

	const dataKeys = ticketTypeInv.map((ticket) => ticket.name);
	let ticketsSold = 0;
	const ticketTypeDataFrame = Object.values(sales).map((date) => {
		const ttPerDate: TicketTypesPerDate = { date: date.date };
		date.ticket_types.forEach((ticketType) => {
			ticketsSold += ticketType.sold;
			ttPerDate[ticketType.name] = ((ttPerDate[ticketType.name] as number) ?? 0) + ticketType.sold;
		});
		return ttPerDate;
	});

	return { ticketTypeDataFrame, dataKeys, ticketsSold };
};

const adjustStart = (isWindowOdd: boolean, counter: number) => {
	// this logic makes sure a more recent value is always added before an older value.
	if ((isWindowOdd && counter % 2 === 0) || (!isWindowOdd && counter > 1 && counter % 2 === 1)) return true;
	else return false;
};

const MIN_WINDOW_SIZE = 5;

// startIdx + size = length or startIdx + size = lastIdx + 1
export const reducer = (state: WindowInfo, action: Action): WindowInfo => {
	if (action.type === 'smart_init') {
		return {
			...state,
			xAxis: action.xAxis,
			windowSize: action.payload,
			windowSizeShadow: action.payload,
			windowStart: 0,
			dataPoints: action.payload,
		};
	} else if (action.type === 'set_timeline') {
		return { ...state, xAxis: action.payload };
	} else if (action.type === 'zoom_in') {
		if (state.windowSize <= MIN_WINDOW_SIZE) {
			return state;
		} else {
			return {
				...state,
				windowSize: state.windowSize - 1,
				windowSizeShadow: state.windowSize - 1,
				windowStart: state.windowStart + 1,
			};
		}
	} else if (action.type === 'zoom_out') {
		if (state.windowSize === state.dataPoints) {
			return state;
		} else if (state.windowSize + state.windowStart === state.dataPoints) {
			return {
				...state,
				windowSize: state.windowSize + 1,
				windowSizeShadow: state.windowSize + 1,
				windowStart: state.windowStart - 1,
			};
		} else {
			return {
				...state,
				windowSize: state.windowSize + 1,
				windowSizeShadow: state.windowSize + 1,
			};
		}
	} else if (action.type === 'move_forward') {
		if (state.windowSize + state.windowStart === state.dataPoints) {
			return state;
		} else {
			return {
				...state,
				windowStart: state.windowStart + 1,
			};
		}
	} else if (action.type === 'move_back') {
		if (state.windowStart === 0) {
			return state;
		} else {
			return {
				...state,
				windowStart: state.windowStart - 1,
			};
		}
	} else if (action.type === 'zoom_input') {
		if (isNaN(action.payload) || action.payload < 5)
			return {
				...state,
				windowSizeShadow: undefined,
			};
		else if (action.payload >= state.dataPoints)
			return {
				...state,
				windowStart: 0,
				windowSize: state.dataPoints,
				windowSizeShadow: state.dataPoints,
			};
		const isWindowOdd = state.windowSize % 2 === 1;
		const midIdx = isWindowOdd ? Math.floor(state.windowSize / 2) : state.windowSize / 2 - 1;
		const center = midIdx + state.windowStart;
		let newSize = 1;
		let newStart = center;
		while (newSize < action.payload) {
			newSize += 1;
			if ((adjustStart(isWindowOdd, newSize) && newStart > 0) || newStart + newSize === state.dataPoints) newStart -= 1;
		}

		return {
			...state,
			windowSize: newSize,
			windowSizeShadow: newSize,
			windowStart: newStart,
		};
	} else if (action.type === 'init') {
		return {
			...state,
			windowSize: action.payload,
			windowSizeShadow: action.payload,
			windowStart: 0,
			dataPoints: action.payload,
		};
	} else if (action.type === 'set_window') {
		return {
			...state,
			windowStart: action.start,
			windowSize: action.size,
			windowSizeShadow: action.size,
		};
	} else return state;
};

export const TICKETS_SOLD = 'ticketsSold';
